// The camera looks a little from above: a cut facing up shows its stump face,
// one facing down the skin wrapping round under it, one facing sideways is edge-on.
import { FRAME, artRuns, boneCrossing, cutPolylines, insetCell, mulberry32, pointAlong, polylineLength } from './goreGeometry.js';
import { PALETTE, bar, blotches, boneWidth, ribs, skullEdge, speckles, stains } from './goreShapes.js';

const RIM = 3.2;

const capDepth = half => Math.max(2.4, Math.min(6.5, half * 0.27));

export const facing = (a, b, up) => {
	const dot = ((b.y - a.y) * up.x - (b.x - a.x) * up.y) / (Math.hypot(b.x - a.x, b.y - a.y) || 1);
	return dot > 0.42 ? 'up' : dot < -0.42 ? 'down' : 'side';
};

const skinTone = (art, run) => {
	let r = 0, g = 0, b = 0, n = 0;
	for (const t of [0.2, 0.35, 0.5, 0.65, 0.8]) {
		const p = pointAlong(run, t);
		for (const depth of [4, 6, 8]) {
			const x = p.x - p.ty * depth;
			const y = p.y + p.tx * depth;
			if (art.alphaAt(x, y) < 0.95) continue;
			const c = art.colorAt(x, y);
			const cr = c >> 16, cg = (c >> 8) & 255, cb = c & 255;
			if (cr + cg + cb < 120) continue;
			r += cr; g += cg; b += cb; n++;
		}
	}
	if (!n) return 0xd8b090;
	return (Math.round(r / n) << 16) | (Math.round(g / n) << 8) | Math.round(b / n);
};

const bulgeDownCuts = (poly, art, up) => {
	const out = [];
	for (let i = 0, n = poly.length; i < n; i++) {
		const a = poly[i];
		const b = poly[(i + 1) % n];
		out.push(a);
		if (a.cut === FRAME || facing(a, b, up) !== 'down') continue;
		const length = Math.hypot(b.x - a.x, b.y - a.y) || 1;
		const ox = (b.y - a.y) / length;
		const oy = -(b.x - a.x) / length;
		for (const run of artRuns([a, b], art.alphaAt)) {
			const start = run[0];
			const end = run[run.length - 1];
			const depth = capDepth(Math.hypot(end.x - start.x, end.y - start.y) / 2);
			for (let k = 0; k <= 12; k++) {
				const t = k / 12;
				const bulge = depth * Math.sin(Math.PI * t);
				out.push({x: start.x + (end.x - start.x) * t + ox * bulge, y: start.y + (end.y - start.y) * t + oy * bulge, cut: a.cut});
			}
		}
	}
	return out;
};

const cap = (shapes, art, run, bone, inside, organs, random, down) => {
	const a = run[0];
	const b = run[run.length - 1];
	const half = Math.hypot(b.x - a.x, b.y - a.y) / 2;
	if (half < 2) return;
	const x = (a.x + b.x) / 2;
	const y = (a.y + b.y) / 2;
	const rotation = Math.atan2(b.y - a.y, b.x - a.x);
	const ry = capDepth(half);
	const ux = Math.cos(rotation), uy = Math.sin(rotation);
	const near = (-uy * down.x + ux * down.y) >= 0 ? 1 : -1;
	const vx = -uy * near, vy = ux * near;
	const at = (along, across) => ({x: x + ux * half * along + vx * ry * across, y: y + uy * half * along + vy * ry * across});
	const ellipse = (centre, rx, ryy, fill, extra = {}) => shapes.cap.push({type: 'ellipse', x: centre.x, y: centre.y, rx, ry: ryy, rotation, fill, ...extra});
	const ring = (scale, fill, extra = {}) => ellipse({x, y}, half * scale, ry * scale, fill, extra);
	ring(1, skinTone(art, run), {stroke: PALETTE.outline, strokeWidth: 1.1});
	ring(0.9, PALETTE.fat);
	ring(0.82, PALETTE.meatDark);
	ellipse(at(0, -0.07), half * 0.8, ry * 0.72, PALETTE.meat);
	ellipse(at(-0.32, -0.28), half * 0.34, ry * 0.26, PALETTE.meatBright);
	ellipse(at(-0.4, -0.36), half * 0.12, ry * 0.1, PALETTE.meatSheen);
	const hit = bone ? boneCrossing(bone, a, b, 0) : null;
	if (hit) {
		const width = boneWidth(bone);
		const squash = Math.max(0.45, ry / half);
		for (const angle of [0.6, 2.2, 3.9, 5.2]) {
			const end = at(Math.cos(angle) * 0.74, Math.sin(angle) * 0.68);
			shapes.cap.push({type: 'line', points: [hit, {x: (hit.x + end.x) / 2 + (random() - 0.5), y: (hit.y + end.y) / 2 + (random() - 0.5)}, end], width: 0.6, color: PALETTE.meatDark});
		}
		ellipse(hit, width / 2, width / 2 * squash, PALETTE.bone, {stroke: PALETTE.boneLine, strokeWidth: 0.8});
		ellipse(hit, width * 0.26, width * 0.26 * squash, PALETTE.marrow);
		ellipse(hit, width * 0.1, width * 0.1 * squash, PALETTE.meatDark);
	}
	if (inside === 'skull') {
		ring(0.7, PALETTE.bone, {stroke: PALETTE.boneLine, strokeWidth: 0.7});
		if (organs) {
			ring(0.6, PALETTE.fibre, {stroke: PALETTE.fibreDark, strokeWidth: 0.6});
			shapes.cap.push({type: 'line', points: [at(0, -0.55), at(0.04, 0), at(0, 0.55)], width: 0.7, color: PALETTE.fibreDark});
			for (const t of [-0.42, -0.24, 0.22, 0.4]) {
				shapes.cap.push({type: 'line', points: [at(t, -0.32), at(t + 0.05, -0.05), at(t - 0.04, 0.18), at(t + 0.03, 0.36)], width: 0.6, color: PALETTE.fibreDark});
			}
		} else {
			ring(0.6, PALETTE.meat);
		}
	}
	if (inside === 'ribs' || inside === 'guts') {
		const back = ux >= 0 ? -1 : 1;
		const spine = at(back * 0.6, 0);
		ellipse(spine, half * 0.12, ry * 0.4, PALETTE.bone, {stroke: PALETTE.boneLine, strokeWidth: 0.7});
		ellipse(spine, half * 0.05, ry * 0.17, PALETTE.marrow);
		if (inside === 'ribs') {
			for (let i = 0; i < 8; i++) {
				const angle = (i + 0.5) / 8 * Math.PI * 2;
				if (Math.abs(Math.cos(angle) * back - 1) < 0.15) continue;
				ellipse(at(Math.cos(angle) * 0.7, Math.sin(angle) * 0.62), half * 0.06, ry * 0.15, PALETTE.bone, {stroke: PALETTE.boneLine, strokeWidth: 0.5});
			}
			if (organs) {
				ellipse(at(-back * 0.1, -0.1), half * 0.24, ry * 0.36, PALETTE.lung, {stroke: PALETTE.lungDark, strokeWidth: 0.6});
				ellipse(at(-back * 0.42, 0.12), half * 0.12, ry * 0.26, PALETTE.liver);
			}
		} else if (organs) {
			for (const [along, across, size] of [[-0.05, -0.2, 0.2], [0.25, 0.1, 0.17], [-0.32, 0.2, 0.15], [0.42, -0.25, 0.13]]) {
				ellipse(at(-back * along, across), half * size, ry * size * 2.2, PALETTE.gut, {stroke: PALETTE.gutDark, strokeWidth: 0.6});
			}
		}
	}
	for (let i = 0; i < 4; i++) {
		const angle = random() * Math.PI * 2;
		const p = at(Math.cos(angle) * 0.72, Math.sin(angle) * 0.62);
		shapes.cap.push({type: 'circle', x: p.x, y: p.y, r: 0.5 + random() * 0.6, fill: PALETTE.meatBright});
	}
	ellipse(at(0.12, 0.6), half * 0.42, ry * 0.16, PALETTE.meatBright);
	const runs = half > 8 ? 2 : 1;
	for (let i = 0; i < runs; i++) {
		const start = at((random() - 0.5) * 0.7, 0.86);
		const reach = 3 + random() * 6;
		const end = {x: start.x + down.x * reach, y: start.y + down.y * reach};
		const width = 1.6 + random() * 0.8;
		shapes.runs.push({type: 'line', points: [start, {x: (start.x + end.x) / 2 + (random() - 0.5) * 0.8, y: (start.y + end.y) / 2}, end], width, color: PALETTE.meatBright});
		shapes.runs.push({type: 'circle', x: end.x, y: end.y, r: width * 0.75, fill: PALETTE.meatBright});
	}
};

const drips = (shapes, run, random, down) => {
	const count = Math.max(1, Math.round(polylineLength(run) / 12));
	for (let i = 0; i < count; i++) {
		const p = pointAlong(run, 0.3 + random() * 0.4);
		const reach = 1.5 + random() * random() * 6;
		const end = {x: p.x + down.x * reach, y: p.y + down.y * reach};
		const width = 1.3 + random() * 0.8;
		shapes.runs.push({type: 'line', points: [p, end], width, color: PALETTE.meatBright});
		shapes.runs.push({type: 'circle', x: end.x, y: end.y, r: width * 0.8, fill: PALETTE.meatBright});
	}
};

const sideBone = (shapes, art, bone, cell, faces, rim) => {
	const poly = cell.poly;
	for (let i = 0, n = poly.length; i < n; i++) {
		const a = poly[i];
		if (a.cut === FRAME || faces[a.cut] !== 'side') continue;
		const b = poly[(i + 1) % n];
		const hit = boneCrossing(bone, a, b);
		if (!hit || art.alphaAt(hit.x, hit.y) < 0.9) continue;
		const length = Math.hypot(b.x - a.x, b.y - a.y);
		const dir = bone.ux * (b.y - a.y) / length - bone.uy * (b.x - a.x) / length >= 0 ? 1 : -1;
		const deep = {x: hit.x - bone.ux * dir * rim * 0.75, y: hit.y - bone.uy * dir * rim * 0.75};
		shapes.bone.push({type: 'poly', points: bar(deep, hit, boneWidth(bone)), fill: PALETTE.bone, stroke: PALETTE.boneLine, strokeWidth: 0.8});
	}
};

// `up` is the art space direction that faces up on screen.
export const slicePiece = (art, cell, lines, {inside, organs, bone, up, blood = PALETTE.meatBright, seed}) => {
	const random = mulberry32(seed);
	const down = {x: -up.x, y: -up.y};
	const faces = {};
	cell.poly.forEach((v, i) => {
		if (v.cut !== FRAME) faces[v.cut] = facing(v, cell.poly[(i + 1) % cell.poly.length], up);
	});
	const rim = RIM * (0.85 + random() * 0.35);
	const outer = bulgeDownCuts(cell.poly, art, up);
	let inner = insetCell(cell, lines, id => faces[id] === 'down' ? 0 : rim);
	if (inner) inner = bulgeDownCuts(inner, art, up);
	const shapes = {band: [], bandTop: [], stains: [], cap: [], runs: [], edges: [], speckles: [], bone: []};
	const innerRuns = [];
	if (inner) for (const line of cutPolylines(inner)) innerRuns.push(...artRuns(line.points, art.alphaAt));
	for (const run of innerRuns) {
		if (inside === 'skull') skullEdge(shapes, run);
		else shapes.bandTop.push({type: 'line', points: run, width: 1.3, color: blood});
		stains(shapes, art, run, random, blood, 0.45, 1.5);
		speckles(shapes, art, inner, run, random, blood, 0.25);
	}
	let capped = false;
	for (const line of cutPolylines(outer)) {
		for (const run of artRuns(line.points, art.alphaAt)) {
			blotches(shapes, run, random, rim * 0.85, 0.35, false);
			if (inside === 'ribs' && faces[line.cut] === 'side') ribs(shapes, art, run, random, rim, true);
			shapes.edges.push({type: 'line', points: run, width: 0.8, color: PALETTE.meatDark});
			if (facing(run[0], run[run.length - 1], up) === 'up') {
				cap(shapes, art, run, bone, inside, organs, random, down);
				capped = true;
			} else {
				drips(shapes, run, random, down);
			}
		}
	}
	if (bone) sideBone(shapes, art, bone, cell, faces, rim);
	return {
		outer,
		inner,
		capped,
		under: shapes.band,
		over: [...shapes.bandTop, ...shapes.stains],
		overlay: [...shapes.speckles, ...shapes.edges, ...shapes.bone, ...shapes.cap, ...shapes.runs],
	};
};
